import { useEffect, useMemo, useRef, useState } from 'react'

export const debounce = <F extends (...args: any[]) => void>(
  func: F,
  delay: number
): ((...args: Parameters<F>) => void) & { cancel: () => void } => {
  let timerId: NodeJS.Timeout

  const debouncedFn = (...args: Parameters<F>) => {
    clearTimeout(timerId)
    timerId = setTimeout(() => func(...args), delay)
  }

  debouncedFn.cancel = () => {
    clearTimeout(timerId)
  }

  return debouncedFn
}

type EffectCallback = () => void

export const useDebounceEffect = (effect: EffectCallback, deps: any[], delay: number): void => {
  const effectRef = useRef(effect)
  effectRef.current = effect

  const debouncedEffect = useMemo(() => {
    return debounce(() => {
      effectRef.current()
    }, delay)
  }, [delay])

  useEffect(() => {
    debouncedEffect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, debouncedEffect])
}
export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}
